export function getRoomNameForCreate(value: string) {
  const name = value.trim().replace(/\s+/g, ' ')
  if (!name) return null
  return name
}

/**
 * Synonyms folded into the room key, reviewed against every prod room-name pair.
 * Must match the server guard (dataverse-plugins EnforceUniqueAccountRoomName.RoomKey),
 * the quick create script (dataverse-webresources forms/accountRoomQuickCreate.js)
 * and dataverse-scripts room_key, or the add-room row offers a create the server
 * then rejects.
 */
const ROOM_NAME_SYNONYMS: Record<string, string> = {
  house: 'home',
  master: 'primary',
  bedroom: 'bed',
  bathroom: 'bath',
  rm: 'room',
  hallway: 'hall',
}

/**
 * Comparison key, not a display form: ignores case, spacing and punctuation and
 * folds the synonyms above, so "Whole Home" and "Whole House" collide.
 */
export function normalizeRoomName(value: string) {
  return value
    .toLowerCase()
    .replace(/['\u2018\u2019]/g, '')
    .split(/[^0-9a-z]+/)
    .filter(Boolean)
    .map((token) => (Object.hasOwn(ROOM_NAME_SYNONYMS, token) ? ROOM_NAME_SYNONYMS[token] : token))
    .join('')
}

/** Structural shape of the fields collision detection reads off a room. */
type RoomNameCandidate = {
  dsy_accountroomid: string
  dsy_name: string
  _dsy_parentroom_value?: string | null
}

/**
 * Bare room labels are the only identity several deferred consumers have
 * (field-frontend CO marks, the NetSuite room columns, CSV export), so a
 * duplicate degrades them. Nesting makes duplicates *reachable* — "Bar" under
 * "Cabana" and "Bar" under "Pool" are legitimately different rooms.
 *
 * Result:
 *   - `'sibling'`  — a room with the same name already sits in the same group
 *                    (same parent, or both top-level). Blocked at create; warned
 *                    on rename, which cannot be blocked without stranding data.
 *   - `'other-group'` — same name elsewhere in the account. Allowed, warned:
 *                    the bare-label consumers cannot tell the two apart.
 */
export function getRoomNameCollision({
  name,
  rooms,
  parentRoomId = null,
  excludeRoomId,
}: {
  name: string
  rooms: RoomNameCandidate[] | undefined | null
  parentRoomId?: string | null
  excludeRoomId?: string | null
}): 'sibling' | 'other-group' | null {
  const key = normalizeRoomName(name)
  if (!key) return null

  let elsewhere = false
  for (const room of rooms ?? []) {
    if (room.dsy_accountroomid === excludeRoomId) continue
    if (normalizeRoomName(room.dsy_name) !== key) continue
    if ((room._dsy_parentroom_value ?? null) === (parentRoomId ?? null)) return 'sibling'
    elsewhere = true
  }

  return elsewhere ? 'other-group' : null
}

/**
 * The name of the sibling a new room would duplicate, so messages name the room
 * that exists ("Whole House") rather than the text typed ("Whole Home").
 */
export function findSiblingRoomName({
  name,
  rooms,
  parentRoomId = null,
}: {
  name: string
  rooms: RoomNameCandidate[] | undefined | null
  parentRoomId?: string | null
}): string | null {
  const key = normalizeRoomName(name)
  if (!key) return null
  const match = (rooms ?? []).find(
    (room) =>
      normalizeRoomName(room.dsy_name) === key && (room._dsy_parentroom_value ?? null) === (parentRoomId ?? null)
  )
  return match ? match.dsy_name.trim() : null
}

/**
 * What the add-room popover owes the name currently typed into it.
 *
 * Three outcomes, and the middle one is the point: a name that collides with a
 * sibling used to produce NOTHING — the create row hidden, the stock suggestion
 * already filtered out of the list, and an empty-state hint ("Type in a name to
 * add room") that contradicts the name sitting in the input. The refusal has to
 * be visible where the refusal happens, so it is a row of its own.
 *
 *   - `none`               — nothing typed, or a create for this name is already
 *                            in flight (its row would create it twice)
 *   - `sibling-collision`  — a disabled row saying so; the create is refused
 *   - `create`             — the "+ Create" row
 *
 * `pendingNames` holds {@link normalizeRoomName} keys.
 */
export type AddRoomInputState =
  | { kind: 'none'; name: null }
  | { kind: 'create'; name: string }
  | { kind: 'sibling-collision'; name: string; existing: string }

export function resolveAddRoomInput({
  value,
  rooms,
  parentRoomId = null,
  pendingNames,
}: {
  value: string
  rooms: RoomNameCandidate[] | undefined | null
  parentRoomId?: string | null
  pendingNames?: ReadonlySet<string>
}): AddRoomInputState {
  const name = getRoomNameForCreate(value)
  if (!name || !rooms) return { kind: 'none', name: null }
  const existing = findSiblingRoomName({ name, rooms, parentRoomId })
  if (existing) return { kind: 'sibling-collision', name, existing }
  if (pendingNames?.has(normalizeRoomName(name))) return { kind: 'none', name: null }
  return { kind: 'create', name }
}

/**
 * The stock names the add-room control offers, in the order a house is walked
 * rather than alphabetically — the list is scanned, not searched.
 */
export const DEFAULT_ROOM_NAMES = [
  'Whole House',
  'Family Room',
  'Kitchen',
  'Living Room',
  'Dining Room',
  'Master Bedroom',
  'Master Bathroom',
  'Bed One',
  'Bed Two',
  'Bed Three',
  'Bed Four',
  'Office',
  'Foyer',
  'Upper Hall',
  'Lower Hall',
  'Porch',
  'Garage',
  'Loggia',
  'Backyard',
  'Equipment Closet',
  'Front Driveway',
]

/**
 * The stock names still worth offering for a new room in `parentRoomId`'s group.
 *
 * Scoped to SIBLINGS — the same scope `getRoomNameCollision` blocks on — so the
 * list offers exactly the names that would be accepted and hides exactly the
 * ones that would be refused.
 *
 * For a top-level room that is unchanged: siblings are the parentless rooms.
 * For a room being added INSIDE another it has to be sibling-scoped to make
 * sense at all: an account-wide filter would hide "Kitchen" from every room's
 * add-inside list the moment one top-level "Kitchen" existed, even though
 * "Kitchen" inside "Guest House" is a legal, ordinary room.
 */
export function getRoomNameSuggestions({
  rooms,
  parentRoomId = null,
}: {
  rooms: RoomNameCandidate[] | undefined | null
  parentRoomId?: string | null
}) {
  const taken = new Set(
    (rooms ?? [])
      .filter((room) => (room._dsy_parentroom_value ?? null) === (parentRoomId ?? null))
      .map((room) => normalizeRoomName(room.dsy_name))
  )
  return DEFAULT_ROOM_NAMES.filter((name) => !taken.has(normalizeRoomName(name)))
}
